import React, { useEffect, useState } from "react";
import { Loader2, Search, X } from "lucide-react";
import { searchUsers } from "../../api/billing";
import type { UserSummary } from "./types";
import { errorMessage, inputClass } from "./ui";

const SEARCH_DEBOUNCE_MS = 400;

const displayName = (u: UserSummary) => [u.firstName, u.lastName].filter(Boolean).join(" ").trim();

/** Find a user by email or name; the parent only ever sees a chosen user. */
export const UserPicker: React.FC<
  Readonly<{ value: UserSummary | null; onChange: (user: UserSummary | null) => void; disabled?: boolean }>
> = ({ value, onChange, disabled }) => {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<UserSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState("");

  useEffect(() => {
    const query = term.trim();
    if (query.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const users = await searchUsers(query);
        if (!cancelled) {
          setResults(users);
          setSearched(query);
        }
      } catch (e) {
        if (!cancelled) setError(errorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [term]);

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-sm">
        <div className="min-w-0">
          <p className="truncate font-medium text-gray-900">{value.email}</p>
          <p className="truncate text-xs text-gray-500">
            {displayName(value) || "No name"} · user #{value.id}
          </p>
        </div>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(null)}
          aria-label="Clear user"
          className="rounded-lg p-1 text-gray-500 hover:bg-violet-100 disabled:opacity-50"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
      <input
        className={`${inputClass} pl-9`}
        placeholder="Search by email or name"
        value={term}
        disabled={disabled}
        onChange={(e) => setTerm(e.target.value)}
      />
      {loading && <Loader2 className="absolute right-3 top-2.5 w-4 h-4 animate-spin text-violet-600" />}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      {results.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {results.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                onClick={() => {
                  onChange(u);
                  setTerm("");
                  setResults([]);
                }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
              >
                <span className="block truncate font-medium text-gray-900">{u.email}</span>
                <span className="block truncate text-xs text-gray-500">
                  {displayName(u) || "No name"} · #{u.id}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {!loading && searched === term.trim() && searched.length >= 2 && results.length === 0 && !error && (
        <p className="mt-1 text-xs text-gray-400">No users found.</p>
      )}
    </div>
  );
};
